import React, { useEffect, useState } from 'react';
import Text from 'components/_UI/Text';
import styled, { keyframes } from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import breakpoints from 'theme/breakpoints';
import userApi from 'api/requests/user';
import colors from 'theme/colors';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import EventCardV2 from '../card/EventCardV2';

export default function EventsVisitedV2({ onEventClick, onLoadEmpty }) {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);

    useEffect(() => {
        getVisitedEvents();
    }, []);

    async function getVisitedEvents() {
        setLoading(true);
        try {
            const { data: res } = await userApi.visitedEvents(5);
            const items = res?.data || [];
            setEvents(items);
            if (items.length === 0) onLoadEmpty?.();
        } catch (error) {
            setEvents([]);
            onLoadEmpty?.();
        } finally {
            setLoading(false);
        }
    }

    return (
        <VisitedContainer>
            {loading ? (
                <SkeletonContainer>
                    {[1, 2, 3, 4, 5].map((_, i) => (
                        <EventCardSkeleton key={i} />
                    ))}
                </SkeletonContainer>
            ) : events.length === 0 ? null : (
                <>
                    <Row>
                        <Text>{'Você visitou'}</Text>
                        <Text type='bold' color={colors.occasionalPurple}>
                            &nbsp;{events.length}
                        </Text>
                        <Text>
                            &nbsp;{events.length === 1 ? 'evento recentemente' : 'eventos recentemente'}
                        </Text>
                    </Row>

                    <CardsContainer>
                        {events.map((event, index) => (
                            <AnimatedCard key={event.slug} $index={index}>
                                <EventCardV2
                                    whiteBackground={true}
                                    data={event}
                                    onEventClick={() => onEventClick(event)}
                                />
                            </AnimatedCard>
                        ))}
                    </CardsContainer>
                </>
            )}
        </VisitedContainer>
    );
}

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const AnimatedCard = styled.div`
    width: 100%;
    animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 50, 300)}ms;
`;

const VisitedContainer = styled(Column)`
    margin-top: 16px;
`;

const CardsContainer = styled(Row)`
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        overflow-y: hidden;
        padding-bottom: 0;

        & > div {
            width: 31%;
            min-width: unset;
            margin-left: 1.5%;
        }

        & > div:nth-child(3n+1) {
            width: 31%;
            margin-left: 0 !important;
        }

        & > div:first-child {
            width: 31%;
            margin-left: 0 !important;
        }
    }
`;

const SkeletonContainer = styled(Row)`
    margin-top: 16px;
    flex-wrap: nowrap;
    overflow-x: hidden;

    & > div {
        min-width: 75vw;
        width: 75vw;
        flex-shrink: 0;
        margin-left: 12px;
    }

    & > div:first-child {
        margin-left: 0;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-wrap: wrap;
        overflow-x: unset;
        margin-top: 30px;

        & > div {
            width: 31.5%;
            min-width: unset;
            margin-left: 1.5%;
            margin-top: 20px;
            & > div {
                width: 100%;
            }
        }

        & > div:first-child {
            margin-left: 1.5%;
        }
    }
`;
